<template>
    <div id="act_form" v-if="formStructure" class="mx-0 mx-md-4">
        <div style="margin: 10px 0 5px 0;">
            <strong>{{ formStructure.title }}</strong>
        </div>
        <section v-for="section in formStructure.sections" :key="section.id" :data-id="section.id">
            <div style="margin: 10px 0 5px 0;">
                <strong>{{ section.title }}</strong>
            </div>
            <div v-if="section.tables">
                <div v-for="table in section.tables" :key="table.title" :data-id="section.id"
                    :data-addable="table.addable" :data-table="table.table">
                    <div :class="['table-responsive', { 'act-stack': isStackedTable(table) }]">

                        <table class="table table-sm">
                            <thead v-if="table.columns">
                                <tr>
                                    <th v-for="column in table.columns" :key="column.label">{{ column.label }}</th>
                                    <th v-if="table.addable"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, i) in table.rows" :key="i"   :class="{'row-edited': table.table && isLastEditedRow(section.id, table.title, i), 'act-header-row': !rowHasInputs(table, row)}">
                                    <input v-if="row.id" type="hidden" name="id" :value="row.id" />
                                    <td v-for="(cell, index) in row.cells" :key="index" :colspan="cell.colspan || 1"
                                        :class="cellClass(table, cell, index)"
                                        :style="isStackedTable(table) ? mobileCellStyle(table, cell, index) : null"
                                        :data-label="isStackedTable(table) && cellType(table, cell, index) ? resolveCellLabel(table, i, index) : null">
                                        <template
                                            v-if="(cell.type || (table.columns && table.columns[index].type)) && !(table.columns && table.columns[index].disabled_if_id && row.id) && !(table.columns && table.columns[index].disabled)">
                                            <component
                                                :is="getInputComponent(cell.type || (table.columns && table.columns[index].type))"
                                                :name="cell.name || (table.columns && table.columns[index].name)"
                                                :value="cell.value || (cell.type === 'checkbox' || (table.columns && table.columns[index].type === 'checkbox') ? false : '')"
                                                :label="cell.label || ''" :text="cell.text || ''"
                                                :placeholder="cell.placeholder || ''" :row-index="i"
                                                :disabled="table.columns && table.columns[index].disabled_if_id && row.id"
                                                :required="cell.required || (table.columns && table.columns[index].required)"
                                                :error="!!cell.error"
                                                :list="datalistIdFor(cell.name || (table.columns && table.columns[index].name))"
                                                @update:value="updateCellValue(section.id, table.title, i, index, $event)" />
                                        </template>
                                        <template v-else>
                                            {{ cell.text || cell.value || '' }}
                                        </template>
                                    </td>
                                    <td class="text-nowrap" v-if="table.addable || table.removable"
                                        :style="isStackedTable(table) ? { '--act-line-size': 1, order: table.mobile_layout.length + 1 } : null">
                                        <button v-if="table.addable" class="btn btn-sm bg-primary-subtle text-primary-emphasis"
                                            type="button" @click="addRow(table, i)">+</button>
                                        <button v-if="table.removable" class="btn btn-sm bg-danger-subtle text-danger-emphasis"
                                            type="button" @click="deleteRow(table, i)">-</button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div v-if="section.divs">
                <div v-for="div in section.divs" :key="div.text" v-html="div.text"></div>
            </div>
        </section>
        <div v-if="insertableContacts.length > 0" class="mt-3">
            <h6>{{ $t('taskAct.customerContacts') }}</h6>
            <ul class="list-unstyled">
                <li v-for="contact in insertableContacts" :key="contact.email || contact.name" class="d-flex align-items-center my-1">
                    <button type="button" class="btn btn-sm bg-primary-subtle text-primary-emphasis me-2" @click="insertContact(contact)">
                        <i class="fa-solid fa-arrow-right-to-bracket"></i>
                    </button>
                    <small><i class="fa-regular fa-user me-2"></i>{{ contact.name }}<span v-if="contact.email"> &lt;{{ contact.email }}&gt;</span></small>
                </li>
            </ul>
        </div>
        <!-- Deliberately outside every <section>: collectFormData scrapes
             '#act_form section' for inputs, and this keeps the options well
             clear of that. -->
        <datalist id="act-contact-names">
            <option v-for="name in contactNameOptions" :key="name" :value="name"></option>
        </datalist>
        <div class="text-danger mt-3">
            {{ $t('taskAct.warning') }}
        </div>
        <div class="action-bar p-3 mt-3">
            <div class="row gy-2">
                <div :class="actionColumnClass">
                    <router-link :to="`/tasks/${$route.params.id}`"
                        class="text-decoration-none btn btn-outline-primary w-100">{{ $t('common.back') }}</router-link>
                </div>
                <div :class="actionColumnClass">
                    <button class="btn btn-primary w-100" @click="saveFormData" :disabled="actionInProgress">{{ $t('common.save') }}</button>
                </div>
                <div :class="actionColumnClass">
                    <button class="btn btn-primary w-100" @click="confirmFormData(false)" :disabled="actionInProgress">{{ $t('common.confirm') }}</button>
                </div>
                <div v-if="can('Priv_AppTasksFollowup')" :class="actionColumnClass">
                    <button class="btn btn-primary w-100" @click="confirmFormData(true)" :disabled="actionInProgress">{{ $t('taskAct.confirmAndSend') }}</button>
                </div>
                <div v-if="canDelete" :class="actionColumnClass">
                    <button class="btn btn-danger w-100" @click="deleteFormData" :disabled="actionInProgress">{{ $t('common.delete') }}</button>
                </div>
            </div>
        </div>
        <!-- Simple Confirm Modal. Outside every <section> for the same reason as
             the datalist above: collectFormData scrapes '#act_form section'. -->
        <div class="modal fade" id="actConfirmModal" tabindex="-1" ref="confirmModal">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-body">
                        <p>{{ confirmMessage }}</p>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-outline-primary" data-bs-dismiss="modal">{{ $t('common.cancel') }}</button>
                        <button type="button" class="btn btn-primary" @click="confirmYes">{{ $t('common.yes') }}</button>
                    </div>
                </div>
            </div>
        </div>
        <!-- Unsaved-changes prompt on leaving the form (beforeRouteLeave). Outside
             every <section> for the same reason as the modal above. -->
        <div class="modal fade" id="actLeaveModal" tabindex="-1" ref="leaveModal">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-body">
                        <p>{{ $t('taskAct.unsavedChanges') }}</p>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-outline-primary" data-bs-dismiss="modal">{{ $t('common.no') }}</button>
                        <button type="button" class="btn btn-outline-primary" @click="chooseLeave('leave')">{{ $t('common.yes') }}</button>
                        <button type="button" class="btn btn-primary" @click="chooseLeave('save')">{{ $t('taskAct.saveAndExit') }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <NotFound v-else-if="notFound" :item="$t('taskAct.actItem')" />
</template>

<script>
import axios from '@/services/axios';
import { Modal } from 'bootstrap';
import NotFound from '@/components/NotFound.vue';
import InputText from '@/components/inputs/InputText.vue';
import InputCheckbox from '@/components/inputs/InputCheckbox.vue';
import InputTextarea from '@/components/inputs/InputTextarea.vue';
import InputDate from '@/components/inputs/InputDate.vue';
import InputTime from '@/components/inputs/InputTime.vue';
import InputSignature from '@/components/inputs/InputSignature.vue';
import { can as canPerm } from '@/composables/usePerms';
import { setDocumentTitle } from '@/composables/usePageTitle';
import { ACT_TRANSIENT_FIELDS, blankTransientFields } from '@/utils/actFields';

export default {
    data() {
        return {
            formStructure: null,
            notFound: false,
            task: null,
            // Nothing else protects this form: the Back link, the browser's back
            // button and the app's own 426 auto-reload all discard it silently.
            isDirty: false,
            // Shared by save/confirm/delete, and only held while a request is in
            // flight. Confirming twice on purpose is fine and gives two files —
            // this just stops an accidental second click landing mid-request.
            actionInProgress: false,
            lastEdited: {
                sectionId: null,
                tableTitle: null,
                rowIndex: null,
            },
            // Pending confirmAction promise resolver, and the message shown in
            // the modal while it is open.
            confirmResolve: null,
            confirmMessage: '',
            // Button picked in the unsaved-changes modal; askLeave() reads it
            // once the modal has finished hiding.
            leaveChoice: 'stay',
        };
    },
    components: {
        NotFound,
        InputText,
        InputCheckbox,
        InputTextarea,
        InputDate,
        InputTime,
        InputSignature
    },
    computed: {
        // Two routes reach this view: /act/{templateId} is the blank form (POST creates
        // an act), /acts/{actId} is an existing one (POST updates it in place).
        actBasePath() {
            const { id, act_id, template_id } = this.$route.params;
            return act_id
                ? `/tasks/${id}/acts/${act_id}`
                : `/tasks/${id}/act/${template_id}`;
        },
        // `deletable` is a static template flag, so it is true on a blank form too.
        // Nothing exists to delete until the act has been saved and the route carries
        // its id — and DELETE on /act/{templateId} resolves to the template's newest
        // saved act, which on a multi-act template is a different act entirely.
        canDelete() {
            return !!(this.formStructure?.deletable && this.$route.params.act_id);
        },
        // Mirrors TaskDetailView's action bar: past three buttons they go two-up
        // on narrow screens instead of one long line that wraps unevenly.
        // Back / Save / Confirm are always there; the other two are conditional.
        actionColumnClass() {
            const count = 3 + (this.can('Priv_AppTasksFollowup') ? 1 : 0) + (this.canDelete ? 1 : 0);
            return count > 3 ? 'col-6 col-md' : 'col';
        },
        // Everyone on the customer side of the task: its own contact, the
        // customer's contact person, the project card's rows and the maintenance
        // card's. Not project_contacts — those are Forus staff, never the client.
        // `task` is fetched separately from the template, so this has to survive
        // the first render with task null.
        insertableContacts() {
            const all = [
                ...(this.task?.contacts || []),
                ...(this.task?.customer_contacts || []),
                ...(this.task?.project_customer_contacts || []),
                ...(this.task?.maintenance_project_contacts || []),
            ].filter(c => c.email || c.name);
            // The same person can sit on both the customer and the project card.
            return [...new Map(all.map(c => [c.email || c.name, c])).values()];
        },
        // Feeds the #act-contact-names datalist.
        contactNameOptions() {
            const names = this.insertableContacts.map(c => c.name).filter(Boolean);
            return [...new Set(names)];
        },
    },
    methods: {
        can(perm) {
            return canPerm(perm);
        },
        isLastEditedRow(sectionId, tableTitle, rowIndex) {
            return (
                this.lastEdited.sectionId === sectionId &&
                this.lastEdited.tableTitle === tableTitle &&
                this.lastEdited.rowIndex === rowIndex
            );
        },
        async fetchTemplate() {
            try {
                this.notFound = false;
                const { id } = this.$route.params;
                setDocumentTitle(this.$t('taskDetail.taskNumber', { id }));
                const response = await axios.get(this.actBasePath);
                this.formStructure = response.data.data;
                this.isDirty = false;
            } catch (error) {
                console.error('Error fetching objects:', error);
                if (error?.response?.status === 404) {
                    this.notFound = true;
                }
            }
        },
        async fetchTask() {
            try {
                const { id } = this.$route.params;
                const response = await axios.get(`tasks/${id}`);
                this.task = response.data.data;
            } catch (error) {
                console.error('Error fetching task:', error);
            }
        },
        // The input type of a cell, wherever the template put it.
        cellType(table, cell, index) {
            return cell.type || (table.columns && table.columns[index] && table.columns[index].type) || null;
        },
        rowHasInputs(table, row) {
            return row.cells.some((cell, index) => this.cellType(table, cell, index));
        },
        // Phones restack a table only when its template says how, via
        // "mobile_layout": an array of lines, each a list of input names, e.g.
        // [["technician_date", "technician_start"], ["technician_signature"]].
        // cell.class plus whatever the column's class_by_value maps this
        // cell's value to, e.g. {"E": "text-red"}.
        cellClass(table, cell, index) {
            const column = table.columns && table.columns[index];
            const map = column && column.class_by_value;
            const value = cell.value ?? cell.text;
            const classes = [cell.class || ''];
            if (map && value != null && map[value]) classes.push(map[value]);
            return classes.join(' ').trim();
        },
        isStackedTable(table) {
            return Array.isArray(table.mobile_layout) && table.mobile_layout.length > 0;
        },
        // Width share and line position for one cell of a stacked table. Flex
        // order moves cells visually only; collectFormData still reads DOM order.
        // An input missing from mobile_layout gets a line of its own at the end.
        mobileCellStyle(table, cell, index) {
            const name = cell.name || (table.columns && table.columns[index] && table.columns[index].name);
            const lineIndex = name ? table.mobile_layout.findIndex(line => line.includes(name)) : -1;
            if (lineIndex === -1) {
                return { '--act-line-size': 1, order: table.mobile_layout.length };
            }
            // Count only the names this table actually has, so a stale entry
            // doesn't leave a gap on the line.
            const names = new Set((table.columns || []).map(c => c.name)
                .concat(...(table.rows || []).map(r => r.cells.map(c => c.name))));
            const size = table.mobile_layout[lineIndex].filter(n => names.has(n)).length;
            return { '--act-line-size': size, order: lineIndex };
        },
        getInputComponent(type) {
            switch (type) {
                case 'checkbox':
                    return 'InputCheckbox';
                case 'textarea':
                    return 'InputTextarea';
                case 'date':
                    return 'InputDate';
                case 'time':
                    return 'InputTime';
                case 'signature':
                    return 'InputSignature';
                default:
                    return 'InputText';
            }
        },
        updateCellValue(sectionId, tableTitle, rowIndex, cellIndex, newValue) {
            const section = this.formStructure.sections.find(sec => sec.id === sectionId);
            if (section) {
                const table = section.tables.find(tbl => tbl.title === tableTitle);
                if (table) {
                    table.rows[rowIndex].cells[cellIndex].value = newValue;
                    this.isDirty = true;

                    // Drop the required-field highlight as soon as the field is
                    // filled. Without this it stayed red until the next Confirm,
                    // long after the 3s alert had gone.
                    const edited = table.rows[rowIndex].cells[cellIndex];
                    if (edited.error && !this.isEmptyValue(newValue)) {
                        delete edited.error;
                    }

                    this.lastEdited = {
                        sectionId,
                        tableTitle,
                        rowIndex,
                    };

                    const cell = table.rows[rowIndex].cells[cellIndex];
                    const column = table.columns ? table.columns[cellIndex] : null;
                    const isCheckbox = cell.type === 'checkbox' || (column && column.type === 'checkbox');
                    const connectedInput = column ? column.connected_input : cell.connected_input;
                    if (isCheckbox && connectedInput && newValue) {
                        table.rows[rowIndex].cells.forEach((otherCell, idx) => {
                            if (idx !== cellIndex) {
                                const otherColumn = table.columns ? table.columns[idx] : null;
                                const otherCellName = otherCell.name || (otherColumn && otherColumn.name);
                                if (otherCellName === connectedInput) {
                                    otherCell.value = false;
                                }
                            }
                        });
                    }
                }
            }
        },
        insertContact(contact) {
            this.setFieldValueByName('client_name', contact.name || '');
            this.setFieldValueByName('contact_person', contact.name || '');
            this.setFieldValueByName('client_email', contact.email || '');
            this.setFieldValueByName('phone', contact.phone || '');
        },
        // Field names that get contact-name type-ahead, resolved the same way
        // setFieldValueByName resolves them. Keyed on the name rather than on the
        // template id, so another template reusing the field gets it for free.
        datalistIdFor(fieldName) {
            return fieldName === 'contact_person' && this.contactNameOptions.length
                ? 'act-contact-names'
                : null;
        },
        setFieldValueByName(fieldName, value) {
            for (const section of this.formStructure.sections) {
                if (!section.tables) continue;
                for (const table of section.tables) {
                    table.rows.forEach((row) => {
                        row.cells.forEach((cell, cellIndex) => {
                            const column = table.columns ? table.columns[cellIndex] : null;
                            const cellName = cell.name || (column && column.name);
                            if (cellName === fieldName) {
                                cell.value = value;
                                this.isDirty = true;
                            }
                        });
                    });
                }
            }
        },
        addRow(table, rowIndex) {
            const newRow = { cells: table.columns.map(() => ({})) };
            table.rows.splice(rowIndex + 1, 0, newRow);
            this.isDirty = true;
        },

        deleteRow(table, rowIndex) {
            table.rows.splice(rowIndex, 1);
            this.isDirty = true;
        },
        // Scrape one <tr> into { name: value }. Shared by both collectors so the
        // checkbox/radio/textarea handling and the name derivation stay identical.
        readRowInputs(rowEl) {
            const rowObject = {};
            const inputs = rowEl.getElementsByTagName('input');
            const textareas = rowEl.getElementsByTagName('textarea');
            for (let j = 0; j < inputs.length; j++) {
                const name = inputs[j].name.replace(/\[\d+\]/, '');
                if (inputs[j].type === 'checkbox') {
                    rowObject[name] = inputs[j].checked ? true : false;
                } else if (inputs[j].type === 'radio') {
                    if (inputs[j].checked) {
                        rowObject[name] = inputs[j].value;
                    }
                } else {
                    rowObject[name] = inputs[j].value;
                }
            }
            for (let k = 0; k < textareas.length; k++) {
                const name = textareas[k].name.replace(/\[\d+\]/, '');
                rowObject[name] = textareas[k].value;
            }
            return rowObject;
        },
        // A table marked data-table: one entry per row.
        collectTableData(div) {
            const rowData = [];
            div.querySelectorAll('table').forEach(table => {
                const rows = table.getElementsByTagName('tbody')[0].getElementsByTagName('tr');
                for (let i = 0; i < rows.length; i++) {
                    rowData.push(this.readRowInputs(rows[i]));
                }
            });
            return rowData;
        },
        // A key-value section (notes, footer, …): every row folded into one object.
        collectFlatData(div) {
            const tableData = {};
            div.querySelectorAll('table').forEach(table => {
                const rows = table.getElementsByTagName('tbody')[0].getElementsByTagName('tr');
                for (let i = 0; i < rows.length; i++) {
                    Object.assign(tableData, this.readRowInputs(rows[i]));
                }
            });
            return tableData;
        },

        collectFormData() {
            const formData = {};
            this.$el.querySelectorAll('#act_form section').forEach(section => {
                const sectionTitle = section.getAttribute('data-id');
                formData[sectionTitle] = sectionTitle === 'optional_notes' || sectionTitle === 'notes' || sectionTitle === 'battery' || sectionTitle === 'footer' ? {} : [];
                // A section renders one div per table. Gather them all and assign
                // once — assigning inside the loop dropped every table but the last.
                const rows = [];
                const flat = {};
                let sawTable = false;
                let sawFlat = false;
                section.querySelectorAll('div[data-id]').forEach(div => {
                    if (div.hasAttribute('data-table')) {
                        sawTable = true;
                        rows.push(...this.collectTableData(div));
                    } else {
                        sawFlat = true;
                        Object.assign(flat, this.collectFlatData(div));
                    }
                });
                if (sawTable) {
                    formData[sectionTitle] = rows;
                } else if (sawFlat) {
                    formData[sectionTitle] = flat;
                }
            });
            return formData;
        },

        // Every customer-signature cell in the form. Matched by field name against
        // ACT_TRANSIENT_FIELDS, never by input type: the technician signature is the
        // same type and IS stored.
        transientSignatureCells() {
            const cells = [];
            (this.formStructure?.sections || []).forEach(section => {
                (section.tables || []).forEach(table => {
                    (table.rows || []).forEach(row => {
                        (row.cells || []).forEach((cell, index) => {
                            const column = table.columns ? table.columns[index] : null;
                            const name = cell.name || (column && column.name);
                            if (ACT_TRANSIENT_FIELDS.includes(name)) {
                                cells.push(cell);
                            }
                        });
                    });
                });
            });
            return cells;
        },
        // InputSignature emits update:value on every stroke, so the cell is in sync
        // with what is drawn on the canvas.
        hasCustomerSignature() {
            return this.transientSignatureCells().some(cell => !!cell.value);
        },
        // Blank the customer signature everywhere it appears in the form, so the
        // canvas clears.
        //
        // Assigns cell.value directly rather than going through updateCellValue,
        // which would set isDirty and undo the flag the caller just cleared.
        clearTransientSignatures() {
            this.transientSignatureCells().forEach(cell => {
                cell.value = '';
            });
        },
        async confirmAction(message) {
            return new Promise((resolve) => {
                this.confirmMessage = message;
                this.confirmResolve = resolve;
                const modalEl = this.$refs.confirmModal;
                // Cancel, ESC and backdrop clicks all just dismiss the modal.
                // Resolve false on any dismissal, otherwise the awaiting caller
                // hangs forever and never clears actionInProgress.
                modalEl.addEventListener('hidden.bs.modal', () => {
                    this.resolveConfirm(false);
                }, { once: true });
                new Modal(modalEl).show();
            });
        },
        // Settles the pending confirm promise once; later calls are no-ops.
        resolveConfirm(answer) {
            if (this.confirmResolve) {
                this.confirmResolve(answer);
                this.confirmResolve = null;
            }
        },
        confirmYes() {
            this.resolveConfirm(true);
            const modal = Modal.getInstance(this.$refs.confirmModal);
            modal.hide();
        },
        // Resolves to 'leave' | 'save' | 'stay'. Resolved on hidden, not on the
        // click, so a follow-up modal (saveFormData's signature confirm) never
        // opens while this one is still animating out. Ei, ESC and the backdrop
        // all leave the default 'stay'.
        askLeave() {
            return new Promise((resolve) => {
                this.leaveChoice = 'stay';
                const modalEl = this.$refs.leaveModal;
                modalEl.addEventListener('hidden.bs.modal', () => {
                    resolve(this.leaveChoice);
                }, { once: true });
                Modal.getOrCreateInstance(modalEl).show();
            });
        },
        chooseLeave(choice) {
            this.leaveChoice = choice;
            Modal.getInstance(this.$refs.leaveModal).hide();
        },
        async saveFormData() {
            // A second click before the first response lands still posts to
            // /act/{templateId} — the route only switches to the new act's own
            // path afterwards — so it would create a second act.
            if (this.actionInProgress) return;
            this.actionInProgress = true;

            // Save deliberately discards the customer signature (see below). Once
            // the customer has signed, Confirm is the step that keeps it, so make
            // Save at that point a conscious choice rather than a silent loss.
            if (this.hasCustomerSignature()
                && !await this.confirmAction(this.$t('taskAct.confirmSaveClearsSignature'))) {
                this.actionInProgress = false;
                return;
            }

            const formData = this.collectFormData();
            // The server discards the customer signature on save (it is only ever
            // rendered into the confirmed PDF), so there is no point uploading a
            // base64 PNG with every save. Confirm still posts it.
            blankTransientFields(formData);
            try {
                const { id } = this.$route.params;
                const response = await axios.post(this.actBasePath, {
                    data: JSON.stringify(formData)
                }, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                this.$root.$refs.globalAlert.showAlert(this.$t('common.saved'), 'success');
                // Match what was just persisted: the signature is gone. Clear it
                // deliberately rather than letting the refetch below blank it as an
                // unexplained side effect. The technician re-signs before confirming.
                this.clearTransientSignatures();
                this.isDirty = false;

                // Posting to /act/{templateId} creates an act and returns its id. Switch
                // to that act's own route, otherwise saving again would create another one.
                const newActId = response.data?.data?.act_id;
                if (!this.$route.params.act_id && newActId) {
                    await this.$router.replace(`/tasks/${id}/acts/${newActId}`);
                }
                await this.fetchTemplate();
            } catch (error) {
                console.error('Error saving form data:', error);
                // Without this the form looked saved when it was not, and the next
                // navigation or the 426 auto-reload took the work with it.
                const message = error?.response?.data?.error?.message || this.$t('common.saveFailed');
                this.$root.$refs.globalAlert?.showAlert(message, 'danger');
            } finally {
                this.actionInProgress = false;
            }
        },
        async deleteFormData() {
            if (this.actionInProgress) return;
            this.actionInProgress = true;

            try {
                const { id } = this.$route.params;
                await axios.delete(this.actBasePath);
                this.isDirty = false;
                this.$router.push(`/tasks/${id}`);
            } catch (error) {
                console.error('Error clearing form data:', error);
                const message = error?.response?.data?.error?.message || this.$t('common.failed');
                this.$root.$refs.globalAlert?.showAlert(message, 'danger');
            } finally {
                this.actionInProgress = false;
            }
        },
        async confirmFormData(sendEmail = false) {
            // Only blocks while the request is in flight — confirming again after
            // it returns is allowed and produces a second file, by design.
            if (this.actionInProgress) return;
            this.actionInProgress = true;

            try {
                // Validate the form data before submission
                const validationErrors = this.validateFormData();
                if (validationErrors.length > 0) {
                    // Display error messages to the user
                    this.displayValidationErrors(validationErrors);
                    return; // Prevent form submission
                }

                // Proceed with form submission if validation passes
                const formData = this.collectFormData();
                // Get client email

                const clientEmailInput = this.$el.querySelector('input[name="client_email"]');
                const clientEmail = clientEmailInput ? clientEmailInput.value : null;

                const { id } = this.$route.params;
                const response = await axios.post(
                    `${this.actBasePath}/confirm`,
                    {
                        data: JSON.stringify(formData),
                        client_email: clientEmail,
                        send_email: sendEmail
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }
                );
                if (response.data && response.data.message) {
                    this.$root.$refs.globalAlert.showAlert(response.data.message, 'success');
                }
                this.isDirty = false;
                this.$router.push(`/tasks/${id}`);
            } catch (error) {
                console.error('Error saving form data:', error);
                const message = error?.response?.data?.error?.message || this.$t('common.failed');
                this.$root.$refs.globalAlert?.showAlert(message, 'danger');
            } finally {
                this.actionInProgress = false;
            }
        },
        // Shared by validateFormData and updateCellValue so the test that raises
        // the highlight and the test that clears it cannot drift apart.
        //
        // `false` is deliberately not empty: a required checkbox left unchecked
        // has always passed validation, and treating it as empty here would start
        // blocking acts that confirm fine today.
        isEmptyValue(value) {
            return value === undefined ||
                value === null ||
                value === '' ||
                (Array.isArray(value) && value.length === 0) ||
                (typeof value === 'object' && Object.keys(value).length === 0);
        },
        // The caption a human would read as this field's name.
        //
        // Templates are hand-authored JSON and put it in one of three places, so
        // all three have to be tried. Returns null when none of them has it --
        // the caller drops the field from the message rather than falling back to
        // the DB column name, which is what produced "technician_date" for users.
        resolveCellLabel(table, rowIndex, cellIndex) {
            // 1. Column layout: cells are empty and everything lives on the column.
            const column = table.columns ? table.columns[cellIndex] : null;
            if (column && column.label) return column.label;

            const cells = table.rows[rowIndex].cells;

            // 2. Header-row layout: a row of static text above the inputs, aligned
            //    by column index. Nearest such row wins, since a table can carry
            //    several input rows under one header.
            for (let r = rowIndex - 1; r >= 0; r--) {
                const above = table.rows[r].cells[cellIndex];
                if (above && !above.type && above.text) return above.text;
            }

            // 3. Label-left layout: "Klient:" sitting in the cell before the input.
            //    Stop at the previous input, whose own caption that would be.
            for (let c = cellIndex - 1; c >= 0; c--) {
                const left = cells[c];
                if (!left) continue;
                if (left.type) break;
                if (left.text) return left.text;
            }

            // 4. Inline checkbox captions ("Jah" / "Ei") and textarea hints.
            const cell = cells[cellIndex];
            return cell.label || cell.placeholder || null;
        },
        validateFormData() {
            const errors = [];

            // Iterate over each section
            for (const section of this.formStructure.sections) {
                // Iterate over each table in the section
                if (section.tables && Array.isArray(section.tables)) {
                    for (const table of section.tables) {
                        // Iterate over each row in the table
                        for (const [rowIndex, row] of table.rows.entries()) {
                            // Iterate over each cell in the row
                            for (const [cellIndex, cell] of row.cells.entries()) {
                                // Determine if the field is required
                                const column = table.columns ? table.columns[cellIndex] : null;
                                const isRequired =
                                    cell.required || (column && column.required) || false;

                                // Check if the required field is empty
                                if (isRequired && this.isEmptyValue(cell.value)) {
                                    // Directly assign the 'error' property
                                    cell.error = true;

                                    // Record the error with field details
                                    errors.push({
                                        sectionTitle: section.title,
                                        tableTitle: table.title,
                                        rowIndex,
                                        cellIndex,
                                        fieldName: cell.name || (column && column.name),
                                        label: this.resolveCellLabel(table, rowIndex, cellIndex)
                                    });
                                } else {
                                    // Clear the error if the field is valid
                                    if (cell.error) {
                                        delete cell.error;
                                    }
                                }
                            }
                        }
                    }
                }
            }

            return errors; // Return the array of validation errors
        },



        // Also fires on window.location.reload(), which is how the 426 handler in
        // services/axios.js refreshes the app out from under an unsaved form.
        warnUnsaved(event) {
            if (!this.isDirty) return;
            event.preventDefault();
            event.returnValue = '';
        },
        displayValidationErrors(errors) {
            // Field names are for the console, never for the alert -- users were
            // being shown "technician_date". Cells whose caption cannot be
            // resolved are simply left out; the red highlight still marks them.
            console.log(errors.map((error) => error.fieldName).join(', '));

            // Several cells can share one column caption, so de-duplicate.
            const labels = [...new Set(errors.map((error) => error.label).filter(Boolean))];
            const message = labels.length
                ? this.$t('taskAct.requiredFieldsMissing', { fields: labels.join(', ') })
                : this.$t('taskAct.requiredFieldsMissingGeneric');

            this.$root.$refs.globalAlert.showAlert(message, 'danger');
        },
    },
    mounted() {
        this.fetchTemplate();
        this.fetchTask();
        window.addEventListener('beforeunload', this.warnUnsaved);
    },
    beforeUnmount() {
        window.removeEventListener('beforeunload', this.warnUnsaved);
    },
    // Covers the Back link and the browser's back button. beforeunload cannot
    // catch those, and neither can catch the other.
    async beforeRouteLeave(to, from, next) {
        if (!this.isDirty) {
            next();
            return;
        }
        const choice = await this.askLeave();
        if (choice === 'leave') {
            this.isDirty = false;
            next();
        } else if (choice === 'save') {
            // Cancel first, then re-navigate: saveFormData() itself navigates
            // (router.replace to the new act's route on a first save), and a
            // navigation started inside a pending guard would cancel this one.
            // A failed save or a declined signature confirm leaves isDirty set,
            // so the user simply stays on the form.
            next(false);
            await this.saveFormData();
            if (!this.isDirty) {
                this.$router.push(to.fullPath);
            }
        } else {
            next(false);
        }
    },
};
</script>

<style>
/*
.table-act {
    width: 100%;
    border-collapse: collapse;
}

.table-act th,
.table-act td {
    padding: 5px;
    text-align: left;
}
*/
.input-wide {
    width: 100%;
}

/*
#act_form {
    width: 100%;
    margin: auto;
}
*/
.is-invalid {
    border-color: red !important;
}
.row-edited td {
  background-color: var(--bs-tertiary-bg) !important;
}
/* Act templates still use the Bootstrap 4 name; Bootstrap 5 calls it fw-bold. */
#act_form .font-weight-bold {
    font-weight: 700;
}
/* Used by act templates' class_by_value; Bootstrap has no text-red. */
#act_form .text-red {
    color: var(--bs-danger);
}

/* Below xl: a table with a mobile_layout drops its header and lays its inputs
   out in the lines the template lists, each input with its own caption. These
   rows (several inputs plus a 480px signature pad) need ~1200px to sit in one
   line, so tablets and small windows stack too. */
@media (max-width: 1199.98px) {
    .act-stack table,
    .act-stack tbody,
    .act-stack td {
        display: block;
        width: 100%;
    }
    .act-stack thead,
    .act-stack .act-header-row {
        display: none;
    }
    .act-stack tr {
        display: flex;
        flex-wrap: wrap;
        border-bottom: 1px solid var(--bs-border-color);
    }
    /* Each line's cells exactly fill the width, so lines break where the
       template's mobile_layout says (--act-line-size = cells on that line).
       Spacing is the cells' own padding, not a flex gap: a gap shows the page
       background through as a grey strip between Bootstrap's painted cells. */
    .act-stack td {
        flex: 0 0 calc(100% / var(--act-line-size, 1));
        width: auto;
        min-width: 0;
        border: 0;
    }
    .act-stack td input {
        min-width: 0;
    }
    .act-stack td[data-label]::before {
        content: attr(data-label);
        display: block;
        font-weight: 600;
        font-size: .875em;
    }
}
</style>